import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { DialogComponent } from '@app/components';
import { Sizes } from '@app/components/platform/dialog/dialog.types';
import { CompanyDocument } from '@app/models/documents/company-document.model';
import { PlatformModule } from '@app/platform.module';
import { FeatureService } from '@app/services/feature.service';
import { DocumentType } from '@documents-v2/enums/document-type.enum';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    standalone: true,
    imports: [CommonModule, PlatformModule, TranslateModule],
    selector: 'app-company-document-deletion-modal',
    template: `
        <ui-dialog [size]="size" (closing)="resolver(false); confirm.open = false" #confirm [header]="header">
            <div>
                @switch (companyDocument?.type) {
                    <!-- offer letter -->
                    @case (DocumentType.offerLetter) {
                        {{ 'document.companyDocumentDeletion.offerLetter.deletingThisTemplateWill' | translate }}
                        <ul>
                            <li>
                                {{ 'document.companyDocumentDeletion.offerLetter.pendingOffersWithdrawn' | translate }}
                            </li>
                            <li>
                                {{
                                    'document.companyDocumentDeletion.offerLetter.pendingSignatureRequestsWithdrawn'
                                        | translate
                                }}
                            </li>
                        </ul>
                    }
                    <!-- document acknowledgement -->
                    @case (DocumentType.acknowledgement) {
                        <p>
                            {{ 'document.companyDocumentDeletion.acknowledgement.ifYouDelete' | translate }}
                        </p>
                    }
                    <!-- signature request -->
                    @case (DocumentType.standardSigningDocument) {
                        <p>
                            {{ 'document.companyDocumentDeletion.signingDocument.ifYouDelete' | translate }}
                        </p>
                    }
                    <!-- view only -->
                    @case (DocumentType.viewOnly) {
                        <p>
                            {{ 'document.companyDocumentDeletion.viewOnly.ifYouDelete' | translate }}
                        </p>
                    }
                }
                <mat-checkbox #canDeleteCheckbox>
                    <span>{{ 'document.iUnderstandThisCannotBeUndone' | translate }}</span>
                </mat-checkbox>
            </div>
            <ui-dialog-footer>
                <ui-button type="ghost" (click)="resolver(false); confirm.open = false">{{
                    'document.cancel' | translate
                }}</ui-button>
                <ui-button
                    type="danger"
                    [disabled]="!canDeleteCheckbox.checked"
                    (click)="resolver(true); confirm.open = false"
                    >{{ 'document.deletePermanently' | translate }}</ui-button
                >
            </ui-dialog-footer>
        </ui-dialog>
    `,
})
export class CompanyDocumentDeletionModal {
    // ui-dialog stuff
    @ViewChild('confirm', { static: true }) confirm: DialogComponent;
    @ViewChild('canDeleteCheckbox') canDeleteCheckbox!: MatCheckbox;
    @Input() size: Sizes = 'small';

    resolver: (shouldReturn: boolean) => void;

    companyDocument: CompanyDocument | null = null;
    header = 'document.deleteTemplate';
    DocumentType = DocumentType;

    constructor(private featureService: FeatureService) {}

    /**
     * Returns a boolean promise that resolve to true
     * if the document should be deleted
     */
    open(companyDocument: CompanyDocument): Promise<boolean> {
        this.canDeleteCheckbox.checked = false;
        this.setHeader(companyDocument);
        this.companyDocument = companyDocument;
        return new Promise((resolve) => {
            this.confirm.open = true;
            this.resolver = resolve;
        });
    }

    private setHeader(companyDocument: CompanyDocument): void {
        if (companyDocument.type === DocumentType.offerLetter) {
            this.header = 'document.deleteAndWithdrawOffers';
        } else if (companyDocument.type === DocumentType.viewOnly) {
            this.header = 'document.deleteViewOnlyDocument';
        } else {
            this.header = 'document.deleteTemplate';
        }
    }
}
